import { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Brain,
  Users,
  TrendingUp,
  Target,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Download,
  Eye,
  Clock,
  Calendar,
  BarChart3,
  Info,
  BookOpen,
  UserPlus,
  GraduationCap,
} from "lucide-react";
import OrganicShape from "./OrganicShape";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../ui/dialog";

const sampleResults = {
  scores: {
    collaboration: 4.2,
    "decision-making": 3.8,
    communication: 3.5,
    sustainability: 4.5,
  },
  overallScore: 4.0,
  completedAt: "2024-01-15T10:30:00Z",
  boardMembers: [
    {
      name: "John Smith",
      collaboration: 4.5,
      decisionMaking: 4.0,
      communication: 3.8,
      sustainability: 4.2,
      overallScore: 4.1,
    },
    {
      name: "Sarah Johnson",
      collaboration: 3.8,
      decisionMaking: 4.2,
      communication: 3.5,
      sustainability: 4.8,
      overallScore: 4.1,
    },
    {
      name: "Michael Chen",
      collaboration: 4.0,
      decisionMaking: 3.5,
      communication: 4.0,
      sustainability: 4.0,
      overallScore: 3.9,
    },
    {
      name: "Emma Davis",
      collaboration: 4.8,
      decisionMaking: 3.2,
      communication: 2.8,
      sustainability: 4.5,
      overallScore: 3.8,
    },
  ],
  recommendations: [
    {
      area: "Meeting Efficiency",
      priority: "High",
      suggestion: "Streamline decision-making with structured agendas",
      implementation:
        "Implement 15-minute focused discussion blocks per agenda item",
      timeframe: "Next board meeting",
      meetingImprovement: "25% faster decisions, clearer outcomes",
    },
    {
      area: "Communication Flow",
      priority: "Medium",
      suggestion: "Pre-meeting briefings for complex sustainability topics",
      implementation: "Send detailed briefs 48 hours before meetings",
      timeframe: "2 weeks",
      meetingImprovement:
        "Better informed discussions, less time spent on explanations",
    },
    {
      area: "Engagement Balance",
      priority: "Medium",
      suggestion: "Rotate meeting facilitation among board members",
      implementation: "Each member leads one section per quarter",
      timeframe: "Next quarter",
      meetingImprovement: "More balanced participation, diverse perspectives",
    },
  ],
};

const safeScore = (value, fallback = 0) => {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Merge API / partial payloads with safe numeric fields (avoids null.toFixed crashes). */
function normalizeDynamicsResults(input) {
  if (!input) return sampleResults;

  const mergedScores = { ...sampleResults.scores, ...(input.scores || {}) };
  const scores = {};
  for (const key of Object.keys(mergedScores)) {
    const v = mergedScores[key];
    scores[key] = safeScore(v, safeScore(sampleResults.scores[key], 0));
  }

  let overallScore = safeScore(input.overallScore, NaN);
  if (!Number.isFinite(overallScore)) {
    overallScore = safeScore(sampleResults.overallScore, 0);
  }

  const boardMembers = Array.isArray(input.boardMembers)
    ? input.boardMembers.map((m) => ({
        name: m.name ?? "—",
        collaboration: safeScore(m.collaboration),
        decisionMaking: safeScore(m.decisionMaking),
        communication: safeScore(m.communication),
        sustainability: safeScore(m.sustainability),
        overallScore: safeScore(m.overallScore),
      }))
    : sampleResults.boardMembers;

  const recommendations = Array.isArray(input.recommendations)
    ? input.recommendations
    : sampleResults.recommendations;

  return {
    ...sampleResults,
    ...input,
    overallScore,
    scores,
    boardMembers,
    recommendations,
    completedAt: input.completedAt || sampleResults.completedAt,
  };
}

export default function BoardDynamicsOptimization({ results }) {
  const displayResults = normalizeDynamicsResults(results);
  const [activeTab, setActiveTab] = useState("overview");
  const [showDynamicsGuide, setShowDynamicsGuide] = useState(false);
  const [showTestInvitation, setShowTestInvitation] = useState(false);

  const getScoreColor = (score) => {
    const s = safeScore(score, 0);
    if (s >= 4.0) return "text-green-600";
    if (s >= 3.0) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreIcon = (score) => {
    const s = safeScore(score, 0);
    if (s >= 4.0) return CheckCircle;
    if (s >= 3.0) return AlertTriangle;
    return Target;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="p-3 rounded-full bg-primary/10">
            <Brain className="w-8 h-8 text-primary" />
          </div>
        </div>
        <div>
          <h3 className="text-2xl font-semibold text-foreground mb-2">
            Board Dynamics Optimization
          </h3>
          <p className="text-foreground/80 max-w-2xl mx-auto">
            Your comprehensive analysis for improving decision-making efficiency
            and board collaboration.
          </p>
          <div className="flex justify-center mt-2">
            <Badge variant="secondary" className="text-xs">
              Completed on {formatDate(displayResults.completedAt)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center">
        <div className="flex bg-muted rounded-lg p-1">
          {[
            { id: "overview", label: "Overview", icon: TrendingUp },
            { id: "members", label: "Board Members", icon: Users },
            { id: "recommendations", label: "Action Items", icon: Target },
            { id: "implementation", label: "Meeting Tips", icon: Clock },
          ].map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              variant={activeTab === id ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab(id)}
              className="flex items-center gap-2"
            >
              <Icon className="w-4 h-4" />
              {label}
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          {/* Overall Score */}
          <OrganicShape className="p-6 bg-gradient-subtle text-center">
            <div className="space-y-2">
              <div className="text-4xl font-bold text-primary">
                {safeScore(displayResults.overallScore, 0).toFixed(1)}/5.0
              </div>
              <div className="text-sm text-foreground/80">
                Overall Board Dynamics Score
              </div>
              <Progress
                value={Math.min(100, Math.max(0, safeScore(displayResults.overallScore, 0) * 20))}
                className="w-48 mx-auto"
              />
            </div>
          </OrganicShape>

          {/* Category Scores */}
          <div className="grid md:grid-cols-2 gap-4">
            {Object.entries(displayResults.scores).map(([category, score]) => {
              const s = safeScore(score, 0);
              const Icon = getScoreIcon(s);
              const categoryLabels = {
                collaboration: "Collaboration",
                "decision-making": "Decision Making",
                communication: "Communication",
                sustainability: "Sustainability Focus",
              };

              return (
                <OrganicShape key={category} className="p-4 bg-gradient-subtle">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${getScoreColor(s)}`} />
                      <div>
                        <div className="font-medium text-sm">
                          {categoryLabels[category]}
                        </div>
                        <div className="text-xs text-foreground/60">
                          {s >= 4.0
                            ? "Excellent"
                            : s >= 3.0
                            ? "Good"
                            : "Needs Improvement"}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`text-lg font-semibold ${getScoreColor(
                        s
                      )}`}
                    >
                      {s.toFixed(1)}
                    </div>
                  </div>
                </OrganicShape>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "members" && (
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h4 className="text-xl font-semibold text-foreground mb-2">
              Board Member Performance
            </h4>
            <p className="text-foreground/80">
              Individual assessment scores across key competencies
            </p>
          </div>

          {/* Member Table with Equalizer Bars */}
          <OrganicShape className="p-6 bg-gradient-subtle">
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Board Member</TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        Collaboration
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">Collaboration Style</p>
                              <p className="text-xs">
                                Reflects approach to team interactions and
                                consensus building.
                              </p>
                              <div className="text-xs pt-1 border-t">
                                <div className="font-medium text-blue-600">
                                  Scores 3.0-5.0:
                                </div>
                                <p>
                                  Naturally collaborative, seeks input from
                                  others, builds team harmony
                                </p>
                                <div className="font-medium text-purple-600 mt-1">
                                  Scores 0-2.9:
                                </div>
                                <p>
                                  Independent thinker, may prefer individual
                                  analysis before group input
                                </p>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        Decision Making
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">
                                Decision Making Style
                              </p>
                              <p className="text-xs">
                                Reflects approach to analyzing information and
                                making strategic choices.
                              </p>
                              <div className="text-xs pt-1 border-t">
                                <div className="font-medium text-blue-600">
                                  Scores 3.0-5.0:
                                </div>
                                <p>
                                  Decisive, comfortable with ambiguity, balances
                                  speed with thoroughness
                                </p>
                                <div className="font-medium text-purple-600 mt-1">
                                  Scores 0-2.9:
                                </div>
                                <p>
                                  Methodical approach, values comprehensive
                                  analysis before deciding
                                </p>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        Communication
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">Communication Style</p>
                              <p className="text-xs">
                                Reflects preferred approach to expressing ideas
                                and facilitating dialogue.
                              </p>
                              <div className="text-xs pt-1 border-t">
                                <div className="font-medium text-blue-600">
                                  Scores 3.0-5.0:
                                </div>
                                <p>
                                  Articulate communicator who naturally
                                  facilitates group discussions
                                </p>
                                <div className="font-medium text-purple-600 mt-1">
                                  Scores 0-2.9:
                                </div>
                                <p>
                                  Thoughtful listener who processes before
                                  speaking, values quality over quantity
                                </p>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        Sustainability
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">
                                Sustainability Perspective
                              </p>
                              <p className="text-xs">
                                Reflects approach to long-term value creation
                                and ESG considerations.
                              </p>
                              <div className="text-xs pt-1 border-t">
                                <div className="font-medium text-blue-600">
                                  Scores 3.0-5.0:
                                </div>
                                <p>
                                  Naturally integrates sustainability into
                                  strategic thinking
                                </p>
                                <div className="font-medium text-purple-600 mt-1">
                                  Scores 0-2.9:
                                </div>
                                <p>
                                  Focused on immediate operational excellence
                                  and financial performance
                                </p>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                    <TableHead className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        Overall
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground hover:text-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">
                                Overall Board Effectiveness
                              </p>
                              <p className="text-xs">
                                Composite score reflecting individual
                                contribution style and board dynamics fit.
                              </p>
                              <div className="text-xs pt-1 border-t">
                                <div className="font-medium text-blue-600">
                                  Scores 3.0-5.0:
                                </div>
                                <p>
                                  Versatile contributor who adapts well to
                                  various board situations
                                </p>
                                <div className="font-medium text-purple-600 mt-1">
                                  Scores 0-2.9:
                                </div>
                                <p>
                                  Specialized contributor who excels in specific
                                  areas or situations
                                </p>
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayResults.boardMembers.map((member, index) => (
                    <TableRow key={member.name}>
                      <TableCell className="font-medium">
                        {member.name}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-16 h-3 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all duration-500"
                              style={{
                                width: `${(member.collaboration / 5) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs font-medium">
                            {member.collaboration.toFixed(1)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-16 h-3 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full transition-all duration-500"
                              style={{
                                width: `${(member.decisionMaking / 5) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs font-medium">
                            {member.decisionMaking.toFixed(1)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-16 h-3 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500 rounded-full transition-all duration-500"
                              style={{
                                width: `${(member.communication / 5) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs font-medium">
                            {member.communication.toFixed(1)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-16 h-3 bg-muted rounded-full overflow-hidden">
                            <div
                              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                              style={{
                                width: `${(member.sustainability / 5) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs font-medium">
                            {member.sustainability.toFixed(1)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Badge
                            variant={
                              member.overallScore >= 4.0
                                ? "default"
                                : member.overallScore >= 3.5
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {member.overallScore.toFixed(1)}
                          </Badge>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>
          </OrganicShape>

          {/* Recommended Education Topics */}
          <div className="grid md:grid-cols-2 gap-4">
            <OrganicShape className="p-4 bg-gradient-subtle">
              <div className="flex items-center gap-3 mb-3">
                <GraduationCap className="w-5 h-5 text-primary" />
                <h5 className="font-semibold">Strategic Leadership</h5>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                  <span className="text-foreground/80">
                    Digital transformation strategies
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                  <span className="text-foreground/80">
                    Risk management frameworks
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                  <span className="text-foreground/80">
                    ESG integration practices
                  </span>
                </div>
              </div>
            </OrganicShape>

            <OrganicShape className="p-4 bg-gradient-subtle">
              <div className="flex items-center gap-3 mb-3">
                <BookOpen className="w-5 h-5 text-blue-500" />
                <h5 className="font-semibold">Board Excellence</h5>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                  <span className="text-foreground/80">
                    Effective board dynamics
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                  <span className="text-foreground/80">
                    Stakeholder engagement
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                  <span className="text-foreground/80">Crisis leadership</span>
                </div>
              </div>
            </OrganicShape>
          </div>
        </div>
      )}

      {activeTab === "recommendations" && (
        <div className="space-y-4">
          <div className="text-center mb-6">
            <h4 className="text-xl font-semibold text-foreground mb-2">
              Practical Action Items
            </h4>
            <p className="text-foreground/80">
              Ready-to-implement improvements for your next board meeting
            </p>
          </div>

          {displayResults.recommendations.map((rec, index) => (
            <OrganicShape key={index} className="p-6 bg-gradient-subtle">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-foreground">
                        {rec.area}
                      </h4>
                      <Badge
                        variant={
                          rec.priority === "High"
                            ? "destructive"
                            : rec.priority === "Medium"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs"
                      >
                        {rec.priority} Priority
                      </Badge>
                    </div>
                    <p className="text-sm text-foreground/80 mb-3">
                      {rec.suggestion}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-primary" />
                      <span className="font-medium text-foreground">
                        How to Implement
                      </span>
                    </div>
                    <p className="text-foreground/70 pl-6">
                      {rec.implementation}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-foreground">
                        Timeline
                      </span>
                    </div>
                    <p className="text-foreground/70 pl-6">{rec.timeframe}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-green-500" />
                      <span className="font-medium text-foreground">
                        Meeting Impact
                      </span>
                    </div>
                    <p className="text-foreground/70 pl-6">
                      {rec.meetingImprovement}
                    </p>
                  </div>
                </div>
              </div>
            </OrganicShape>
          ))}
        </div>
      )}

      {activeTab === "implementation" && (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h4 className="text-xl font-semibold text-foreground mb-2">
              Next Meeting Improvements
            </h4>
            <p className="text-foreground/80">
              Concrete steps to enhance your next board meeting
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <OrganicShape className="p-6 bg-gradient-subtle">
              <h5 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Before the Meeting
              </h5>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-foreground mb-1">
                      Send focused pre-reads
                    </div>
                    <div className="text-foreground/70">
                      Limit to 3 pages max per agenda item
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-foreground mb-1">
                      Set clear decision points
                    </div>
                    <div className="text-foreground/70">
                      Mark agenda items as Discussion vs Decision
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-foreground mb-1">
                      Assign speaking time
                    </div>
                    <div className="text-foreground/70">
                      15 minutes per major topic maximum
                    </div>
                  </div>
                </div>
              </div>
            </OrganicShape>

            <OrganicShape className="p-6 bg-gradient-subtle">
              <h5 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                During the Meeting
              </h5>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-foreground mb-1">
                      Start with decisions
                    </div>
                    <div className="text-foreground/70">
                      Handle urgent decisions first while energy is high
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-foreground mb-1">
                      Use the 'parking lot'
                    </div>
                    <div className="text-foreground/70">
                      Capture off-topic items for later discussion
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-foreground mb-1">
                      Summarize action items
                    </div>
                    <div className="text-foreground/70">
                      End each topic with clear next steps and owners
                    </div>
                  </div>
                </div>
              </div>
            </OrganicShape>
          </div>

          <OrganicShape className="p-6 bg-primary/10">
            <h5 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Meeting Effectiveness Checklist
            </h5>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium text-foreground mb-2">
                  For your next meeting:
                </div>
                <div className="space-y-1 text-foreground/80">
                  <div>□ Agenda items under 15 minutes each</div>
                  <div>□ Pre-read materials sent 48h early</div>
                  <div>□ Decision vs discussion items marked</div>
                  <div>□ Sustainability metrics dashboard ready</div>
                </div>
              </div>
              <div>
                <div className="font-medium text-foreground mb-2">
                  Post-meeting follow-up:
                </div>
                <div className="space-y-1 text-foreground/80">
                  <div>□ Action items with owners documented</div>
                  <div>□ Decision rationale captured</div>
                  <div>□ Next meeting prep assigned</div>
                  <div>□ Member feedback collected</div>
                </div>
              </div>
            </div>
          </OrganicShape>

          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              className="hover:scale-105 transition-transform"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Checklist
            </Button>
            <Button className="hover:scale-105 transition-transform">
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Follow-up
            </Button>
          </div>
        </div>
      )}

      {/* New Board Member Onboarding Guide */}
      <div className="space-y-6 border-t pt-8">
        <div className="text-center mb-6">
          <h4 className="text-xl font-semibold text-foreground mb-2 flex items-center justify-center gap-2">
            <UserPlus className="w-6 h-6 text-primary" />
            New Board Member Onboarding
          </h4>
          <p className="text-foreground/80">
            Resources and guidance for new board members
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <OrganicShape className="p-6 bg-gradient-subtle">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-primary" />
                <h5 className="font-semibold text-foreground">
                  Understanding Your Board Dynamics
                </h5>
              </div>
              <p className="text-sm text-foreground/80">
                Learn how this board works best together, including
                communication styles, decision-making processes, and
                collaboration patterns.
              </p>
              <Dialog
                open={showDynamicsGuide}
                onOpenChange={setShowDynamicsGuide}
              >
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    View Dynamics Guide
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Board Dynamics Guide</DialogTitle>
                    <DialogDescription>
                      Your personalized guide to this board's working style
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h6 className="font-semibold mb-2">
                        Your Board's Strengths
                      </h6>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>• Strong sustainability focus (4.5/5)</div>
                        <div>• Collaborative approach (4.2/5)</div>
                        <div>• Balanced decision-making (3.8/5)</div>
                        <div>• Open communication style (3.5/5)</div>
                      </div>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h6 className="font-semibold mb-2">
                        How to Contribute Effectively
                      </h6>
                      <div className="space-y-2 text-sm">
                        <div>
                          • Prepare thoroughly for sustainability discussions -
                          this board values deep ESG insights
                        </div>
                        <div>
                          • Share your perspectives openly - collaborative input
                          is welcomed and expected
                        </div>
                        <div>
                          • Be patient with decision-making processes -
                          methodical analysis is valued
                        </div>
                        <div>
                          • Ask clarifying questions - this helps the group's
                          communication flow
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => {
                          // Simulate PDF download
                          const link = document.createElement("a");
                          link.href = "#";
                          link.download = "board-dynamics-guide.pdf";
                          link.click();
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF Guide
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowDynamicsGuide(false)}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </OrganicShape>

          <OrganicShape className="p-6 bg-gradient-subtle">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Target className="w-6 h-6 text-green-500" />
                <h5 className="font-semibold text-foreground">
                  Take Your Assessment
                </h5>
              </div>
              <p className="text-sm text-foreground/80">
                Complete your board dynamics assessment to understand your
                working style and how you can best contribute to the team.
              </p>
              <Dialog
                open={showTestInvitation}
                onOpenChange={setShowTestInvitation}
              >
                <DialogTrigger asChild>
                  <Button className="w-full">
                    <Eye className="w-4 h-4 mr-2" />
                    Start Assessment
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Board Dynamics Assessment</DialogTitle>
                    <DialogDescription>
                      Discover your working style and board contribution
                      patterns
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-primary/10 p-4 rounded-lg">
                      <h6 className="font-semibold mb-2">
                        What You'll Discover
                      </h6>
                      <div className="space-y-1 text-sm">
                        <div>• Your collaboration style and preferences</div>
                        <div>• How you approach decision-making</div>
                        <div>
                          • Your communication patterns in group settings
                        </div>
                        <div>• Your sustainability leadership approach</div>
                      </div>
                    </div>
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h6 className="font-semibold mb-2">Assessment Details</h6>
                      <div className="space-y-1 text-sm text-foreground/80">
                        <div>• Duration: 15-20 minutes</div>
                        <div>• Format: Interactive scenarios and questions</div>
                        <div>• Results: Immediate personalized report</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => {
                          // This would normally redirect to the actual assessment
                          alert("Assessment would open in a new window");
                          setShowTestInvitation(false);
                        }}
                      >
                        Begin Assessment
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowTestInvitation(false)}
                      >
                        Maybe Later
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </OrganicShape>
        </div>
      </div>
    </div>
  );
}



